import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useImproveAnswer } from '@/hooks/useBot';
import { IChatQueryRecommendation } from '@/types';
import { handleShowError } from '@/utils';
import ImproveAnswerModal from '@/views/bot-message/components/ImproveAnswerModal';
import { cn } from '@/lib/utils';
import { ExternalLink, Sparkles } from 'lucide-react';
import { FC, useContext, useRef, useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { sanitizeEmailHTML } from '@/utils/htmlSanitizer';
import { ProductsContext } from '../context';
import FeedbackBadge from './FeedbackBadge';
import { MessageSender } from './MessagesContainer';
import { format } from 'date-fns';

interface IMessageItemProps {
  sender: MessageSender;
  content: string;
  aiLogo?: string;
  recommendations?: IChatQueryRecommendation;
  isLoading?: boolean;
  senderName?: string;
  feedback?: boolean;
  previousMessage?: string;
  url?: string;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  timestamp?: string;
  visitorName?: string;
}

interface ImproveAnswersForm {
  trigger_phrase: string;
  bot_response: string;
  feedback: string;
  content_value: string;
}

const MessageItem: FC<IMessageItemProps> = ({
  sender,
  content = '',
  recommendations,
  isLoading,
  feedback,
  previousMessage,
  url,
  isFirstInGroup = true,
  isLastInGroup = true,
  timestamp,
  visitorName,
  senderName,
}) => {
  const { token }: any = useContext(ProductsContext);
  const { mutate: improveAnswer, isPending: isImprovingAnswer } = useImproveAnswer();

  const improveAnswersForm = useForm<ImproveAnswersForm>({
    defaultValues: {
      trigger_phrase: '',
      bot_response: '',
      feedback: '',
      content_value: '',
    },
  });

  const [improveAnswerOpen, setImproveAnswerOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleOpenImproveAnswer = () => setImproveAnswerOpen(true);
  const handleCloseImproveAnswer = () => {
    improveAnswersForm.reset();
    setImproveAnswerOpen(false);
  };

  const handleClickImproveAnswer = () => {
    improveAnswersForm.setValue('trigger_phrase', previousMessage || '');
    improveAnswersForm.setValue('bot_response', content);
    handleOpenImproveAnswer();
  };

  const handleSubmitImproveAnswer = (data: ImproveAnswersForm) => {
    improveAnswer(
      {
        chat_token: token,
        trigger_phrase: data.trigger_phrase,
        feedback: data.feedback,
        content_value: data.content_value,
        content_type: 'text',
      },
      {
        onSuccess: (data) => {
          handleCloseImproveAnswer();
          toast.success(data?.message || 'Answer updated successfully');
        },
        onError: handleShowError,
      },
    );
  };

  useEffect(() => {
    if (!contentRef.current) return;
    const placeholderImage = '/images/image-placeholder.svg';
    const handleImageError = (event: Event) => {
      const img = event.target as HTMLImageElement;
      if (img.src !== placeholderImage && !img.src.includes('image-placeholder')) {
        img.src = placeholderImage;
        img.onerror = null;
        img.style.objectFit = 'cover';
        img.style.backgroundColor = '#f3f4f6';
        img.style.borderRadius = '4px';
        img.alt = img.alt || 'Image not available';
      }
    };

    const images = contentRef.current.querySelectorAll('img');
    images.forEach((img) => {
      if (img.complete && img.naturalHeight === 0 && img.naturalWidth === 0) {
        img.src = placeholderImage;
        img.style.objectFit = 'cover';
        img.style.backgroundColor = '#f3f4f6';
        img.style.borderRadius = '4px';
        img.alt = img.alt || 'Image not available';
      } else {
        img.addEventListener('error', handleImageError);
        img.onerror = handleImageError as OnErrorEventHandler;
      }
    });

    return () => {
      images.forEach((img) => {
        img.removeEventListener('error', handleImageError);
        img.onerror = null;
      });
    };
  }, [content]);

  const isRightAligned = sender === 'human' || sender === 'ai';
  const displayTimestamp = timestamp ? format(new Date(timestamp), 'HH:mm') : null;

  // Clean up content: if it's a human message, it often comes prefixed with "<b>Name</b>: "
  // We strip this because we already show the name in the metadata.
  const displayContent = useMemo(() => {
    // Default param only guards `undefined`, not `null`. Human-message content is
    // sourced from nullable fields (DB item.ai, live socket payloads), so coalesce
    // before calling string methods to avoid crashing the whole dashboard.
    const safeContent = content ?? '';
    if (sender === 'human' && safeContent.includes('</b>: ')) {
      return safeContent.replace(/^<b>.*?<\/b>:\s*/, '');
    }
    return safeContent;
  }, [sender, content]);

  return (
    <>
      <div className={cn(
        "flex flex-col w-full",
        isRightAligned ? "items-end" : "items-start",
        !isFirstInGroup ? "-mt-0.5" : "mt-2"
      )}>
        <div className={cn(
          // Cap message width so a long AI reply doesn't stretch across the
          // entire 700-1000px center column. 85% of pane was too generous on
          // wide screens; absolute cap (640px) gives a comfortable reading
          // measure similar to ChatGPT/Linear regardless of viewport.
          "flex max-w-[min(88%,506px)] gap-2",
          isRightAligned ? "flex-row-reverse" : "flex-row",
          !isFirstInGroup && !isRightAligned && "pl-[34px]"
        )}>
          {isFirstInGroup && sender === 'visitor' ? (
            <div className="mt-auto flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
              {(visitorName || 'Visitor').split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
            </div>
          ) : null}

          <div className={cn(
            "flex flex-col min-w-0",
            isRightAligned ? "items-end" : "items-start"
          )}>
            {/* Metadata — only on the first message of a group */}
            {isFirstInGroup && (
              <div className={cn(
                "mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-slate-400",
              )}>
                {sender === 'human' ? (
                  senderName || 'Agent'
                ) : sender === 'ai' ? (
                  'AI assistant'
                ) : (
                  'Visitor'
                )}
                {displayTimestamp && (
                  <span className="text-slate-300 font-medium normal-case tracking-normal ml-0.5">
                    {displayTimestamp}
                  </span>
                )}
              </div>
            )}

            {/* Bubble */}
            <div className={cn(
              "relative group transition-all duration-200",
              isRightAligned ? "items-end" : "items-start"
            )}>
              {isLoading ? (
                <div className="flex items-center gap-1 rounded-2xl border border-[#D6E2FF] bg-[#EEF3FF] px-4 py-3 shadow-sm">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#0848E7] opacity-40"
                      style={{ animationDelay: `${i * 0.2}s` }}
                    />
                  ))}
                </div>
              ) : (
                <div className={cn(
                  "max-w-[480px] px-3.5 py-2.5 text-[13.5px] font-normal leading-[1.55]",
                  sender === 'visitor'
                    ? "bg-white border border-slate-200 text-slate-800"
                    : sender === 'human'
                    ? "bg-[#04216B] text-white"
                    : "bg-[#0848E7] text-white",
                  // The supplied design keeps the speech-tail corner on every
                  // bubble, including consecutive messages from one sender.
                  isRightAligned ? "rounded-xl rounded-br" : "rounded-xl rounded-bl"
                )}>
                  <div
                    ref={contentRef}
                    className={cn(
                      "break-word chat-inner-html",
                      isRightAligned ? "[&_a]:text-[#D6E2FF] [&_a]:underline" : "[&_a]:text-[#0848E7] [&_a]:underline"
                    )}
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHTML(displayContent) }}
                  />
                  
                  {url && (
                    <div className={cn(
                      "mt-2.5 pt-2.5 border-t flex flex-col gap-1.5",
                      isRightAligned ? "border-white/10" : "border-slate-100"
                    )}>
                      <div className={cn(
                        "flex items-start gap-2 text-[11px]",
                        isRightAligned ? "text-white/80" : "text-slate-500"
                      )}>
                        <ExternalLink size={12} className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="font-bold">Source: </span>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              "break-all font-mono",
                              isRightAligned ? "text-white hover:text-[#EEF3FF]" : "text-[#0848E7] hover:text-[#0639BA]"
                            )}
                          >
                            {url.replace(/^https?:\/\/(www\.)?/, '')}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons — on hover or absolute? Let's keep them visible for AI messages */}
              {sender === 'ai' && !isLoading && (
                <div className="flex items-center gap-2 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={handleClickImproveAnswer}
                    className="px-2 py-0.5 rounded-full border border-slate-200 bg-white text-[10px] font-bold text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-1 shadow-sm transition-all"
                  >
                    <Sparkles className="w-2.5 h-2.5" />
                    Improve
                  </button>
                  {feedback === false && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-900 text-white text-[9px] font-bold">
                      Negative match
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {feedback !== undefined && sender === 'ai' && (
          <div className="mt-1">
            <FeedbackBadge feedback={feedback} />
          </div>
        )}
      </div>

      <Dialog open={improveAnswerOpen} onOpenChange={setImproveAnswerOpen}>
        <DialogContent className="lg:max-w-[900px] xl:max-w-[1000px]" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Improve answer</DialogTitle>
          </DialogHeader>
          <FormProvider {...improveAnswersForm}>
            <ImproveAnswerModal />
          </FormProvider>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setImproveAnswerOpen(false)}
              disabled={isImprovingAnswer}
            >
              Cancel
            </Button>
            <Button
              onClick={improveAnswersForm.handleSubmit(handleSubmitImproveAnswer)}
              isLoading={isImprovingAnswer}
            >
              Update Answer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessageItem;
